"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cva } from "class-variance-authority";
import * as React from "react";
import { Button } from "@/components/atoms/Button";
import XIcon24 from "@/icons/__generated__/X/X24";
import { cn } from "@/lib/utils";

type DialogProps = DialogPrimitive.DialogProps;
const Dialog = (props: DialogProps) => {
  return <DialogPrimitive.Root {...props} />;
};

type DialogContextValue = {
  size?: "sm" | "md" | "lg" | "full";
};

const DialogContext = React.createContext<DialogContextValue>({});

type DialogTriggerProps = DialogPrimitive.DialogTriggerProps;
const DialogTrigger = DialogPrimitive.Trigger;

type DialogPortalProps = DialogPrimitive.DialogPortalProps;
const DialogPortal = DialogPrimitive.Portal;

type DialogCloseProps = DialogPrimitive.DialogCloseProps & {
  headerAlignment?: "center" | "left";
};
const DialogClose = ({ headerAlignment, ...props }: DialogCloseProps) => {
  return (
    <DialogPrimitive.Close asChild={true} {...props}>
      <Button
        variant="text-only"
        className={cn({
          "tw-absolute tw-right-24 sm:tw-right-32 tw-top-24 sm:tw-top-32":
            headerAlignment === "center",
          "tw-ml-64": headerAlignment !== "center",
        })}
      >
        <XIcon24 />
        <span className="tw-sr-only">Close</span>
      </Button>
    </DialogPrimitive.Close>
  );
};

type DialogOverlayProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Overlay
>;
const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  DialogOverlayProps
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "tw-fixed tw-inset-0 tw-z-[9997] tw-bg-gray-900/80 data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const dialogContentStyles = cva(
  "tw-p-0 tw-gap-0 tw-min-w-full tw-flex tw-flex-col",
  {
    variants: {
      size: {
        sm: "sm:tw-min-w-[400px] tw-min-w-[280px] tw-w-[initial] tw-max-h-[90vh]",
        md: "md:tw-min-w-[600px] md:tw-max-h-[90vh] max-md:tw-top-[32px] max-md:tw-bottom-0 max-md:tw-translate-y-[0%] max-md:tw-max-h-[calc(100vh-32px)] max-md:tw-bottom-0 max-md:tw-translate-y-[0%] max-md:tw-top-[unset]",
        lg: "lg:tw-min-w-[800px] tw-max-h-[90vh] max-lg:tw-max-h-[calc(100vh-32px)] max-lg:tw-bottom-0 max-lg:tw-translate-y-[0%] max-lg:tw-top-[unset]",
        full: "tw-min-w-full tw-min-h-screen tw-h-screen",
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
);

type DialogContentProps = DialogPrimitive.DialogContentProps &
  React.HTMLAttributes<HTMLDivElement> & {
    size?: "sm" | "md" | "lg" | "full";
    className?: string;
    overlayClassName?: string;
    children?: React.ReactNode;
    testId?: string;
  };

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, size, overlayClassName, testId, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay className={overlayClassName} />
    <DialogContext.Provider value={{ size }}>
      <DialogPrimitive.Content
        ref={ref}
        data-testid={testId}
        className={cn(
          "tw-fixed tw-left-[50%] tw-top-[50%] tw-z-[9998] tw-w-full tw-max-w-lg tw-translate-x-[-50%] tw-translate-y-[-50%] tw-gap-16 tw-bg-base-white tw-p-24 tw-shadow-lg tw-duration-200 data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0 data-[state=closed]:tw-zoom-out-95 data-[state=open]:tw-zoom-in-95 data-[state=closed]:tw-slide-out-to-left-1/2 data-[state=closed]:tw-slide-out-to-top-[48%] data-[state=open]:tw-slide-in-from-left-1/2 data-[state=open]:tw-slide-in-from-top-[48%] tw-rounded-4 tw-flex tw-flex-col tw-overflow-auto",
          dialogContentStyles({ size }),
          className,
        )}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogContext.Provider>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

type DialogHeaderProps = React.HTMLAttributes<HTMLDivElement> & {
  showCloseButton?: boolean;
  headerAlignment?: "center" | "left";
  image?: React.ReactElement<{ className?: string }>;
  children?: React.ReactNode;
  asChild?: boolean;
};

const headerStyles = cva("sm:tw-p-32 tw-p-24 tw-relative tw-space-y-0", {
  variants: {
    headerAlignment: {
      center: "tw-flex tw-items-start tw-justify-center",
      left: "tw-flex tw-items-start",
    },
  },
  defaultVariants: {
    headerAlignment: "left",
  },
});

const DialogHeader: React.FC<DialogHeaderProps> = ({
  className,
  children,
  showCloseButton,
  headerAlignment,
  image,
  ...props
}) => (
  <div
    className={cn(
      "tw-flex tw-relative",
      headerStyles({ headerAlignment }),
      className,
    )}
    {...props}
  >
    <div
      className={cn("tw-flex tw-items-start tw-gap-12 tw-flex-1", {
        "tw-justify-start": headerAlignment !== "center",
        "tw-justify-center tw-flex-col tw-items-center tw-text-center":
          headerAlignment === "center",
      })}
    >
      {image && React.isValidElement(image) && (
        <div
          className={cn("tw-flex-shrink-0", {
            "tw-max-w-24 tw-max-h-24 tw-mt-[3px]": headerAlignment !== "center",
            "tw-max-w-48 tw-max-h-48": headerAlignment === "center",
          })}
        >
          {image}
        </div>
      )}
      <div className="tw-w-full">{children}</div>
    </div>

    {showCloseButton && <DialogClose headerAlignment={headerAlignment} />}
  </div>
);

type DialogBodyProps = React.HTMLAttributes<HTMLDivElement> & {
  showScrollBorder?: boolean;
  children?: React.ReactNode;
  className?: string;
};

const DialogBody: React.FC<DialogBodyProps> = ({
  className,
  showScrollBorder,
  children,
  ...props
}) => {
  const [showTopBorder, setShowTopBorder] = React.useState(false);
  const bodyRef = React.useRef<HTMLDivElement>(null);

  const handleScroll = React.useCallback(() => {
    if (!bodyRef.current) return;
    const scrollTop = bodyRef.current.scrollTop;

    setShowTopBorder(scrollTop > 0);
  }, []);

  React.useEffect(() => {
    const bodyElement = bodyRef.current;
    if (!bodyElement) return;

    bodyElement.addEventListener("scroll", handleScroll);
    return () => bodyElement.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  return (
    <div
      ref={bodyRef}
      className={cn(
        "tw-flex-1 tw-overflow-auto sm:tw-px-32 tw-px-24 tw-overflow-y-auto",
        {
          "tw-border-b tw-border-gray-300": showScrollBorder,
          "tw-border-t tw-border-gray-300": showScrollBorder && showTopBorder,
        },
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
};

DialogBody.displayName = "DialogBody";

type DialogFooterProps = React.HTMLAttributes<HTMLDivElement>;
const DialogFooter = ({ className, children, ...props }: DialogFooterProps) => {
  const { size } = React.useContext(DialogContext);

  return (
    <div
      className={cn(
        "tw-flex tw-flex-col-reverse sm:tw-flex-row sm:tw-justify-end md:tw-space-x-8",
        {
          "tw-px-24 tw-py-16": size === "sm",
          "tw-px-32 tw-py-24": size !== "sm",
        },
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
};
DialogFooter.displayName = "DialogFooter";

type DialogTitleProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Title
>;

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  DialogTitleProps
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "tw-headline-3-m tw-text-gray-900 sm:tw-headline-4-m",
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

type DialogDescriptionProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Description
>;
const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  DialogDescriptionProps
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("tw-body-2 tw-text-gray-600 tw-mt-2", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

const bannerStyles = cva(
  "tw-flex tw-w-full sm:tw-px-32 tw-px-24 tw-py-8 tw-items-center tw-justify-center",
  {
    variants: {
      variant: {
        green: "tw-bg-green-800 tw-text-brand-white",
        red: "tw-bg-red-800 tw-text-brand-white",
        black: "tw-bg-brand-black tw-text-brand-white",
        gray: "tw-bg-gray-200",
        yellow: "tw-bg-yellow-200 tw-text-yellow-900",
        white: "tw-bg-base-white tw-text-brand-black",
      },
    },
    defaultVariants: {
      variant: "gray",
    },
  },
);

type DialogBannerProps = React.HTMLAttributes<HTMLDivElement> & {
  variant?: "green" | "red" | "black" | "gray" | "yellow" | "white";
};

const DialogBanner = React.forwardRef<HTMLDivElement, DialogBannerProps>(
  ({ variant, children }, ref) => (
    <div ref={ref} className={bannerStyles({ variant })}>
      {children}
    </div>
  ),
);

DialogBanner.displayName = "DialogBanner";

export {
  Dialog,
  DialogBanner,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  type DialogBannerProps,
  type DialogBodyProps,
  type DialogCloseProps,
  type DialogContentProps,
  type DialogContextValue,
  type DialogDescriptionProps,
  type DialogFooterProps,
  type DialogHeaderProps,
  type DialogOverlayProps,
  type DialogPortalProps,
  type DialogProps,
  type DialogTitleProps,
  type DialogTriggerProps,
};
